import React from 'react';
import DiagnosticCriteriaTable from '../../components/DiagnosticCriteriaTable';
import { useUserInput } from '../../context/UserInputContext';
import {
  mfOvertDiagnosticCriteria,
  mfPrefibDiagnosticCriteria,
  mfPostPvDiagnosticCriteria,
  mfPostEtDiagnosticCriteria,
  evaluatePrimaryMf,
  evaluatePostMf,
  DiagnosisVerdict,
} from '../../data/diseasesData';
import { DiagnosticCriteriaSet } from '../../types';

type MfDiagnosisId = 'overt' | 'prefib' | 'postPv' | 'postEt';

const OPTIONS: { id: MfDiagnosisId; label: string }[] = [
  { id: 'overt', label: 'Primary MF — Overt Fibrotic Stage' },
  { id: 'prefib', label: 'Primary MF — Pre-fibrotic Stage' },
  { id: 'postPv', label: 'Post-Polycythemia Vera MF' },
  { id: 'postEt', label: 'Post-Essential Thrombocythemia MF' },
];

interface MfSection {
  criteria: DiagnosticCriteriaSet;
  subtitle: string;
  stateKey: 'mfOvertCriteria' | 'mfPrefibCriteria' | 'mfPostPvCriteria' | 'mfPostEtCriteria';
  evaluate: (majorMet: number[], minorMet: number[]) => DiagnosisVerdict;
  majorTitle?: string;
  minorTitle?: string;
}

const SECTION_MAP: Record<MfDiagnosisId, MfSection> = {
  overt: {
    criteria: mfOvertDiagnosticCriteria,
    subtitle: 'ICC Criteria',
    stateKey: 'mfOvertCriteria',
    evaluate: evaluatePrimaryMf,
  },
  prefib: {
    criteria: mfPrefibDiagnosticCriteria,
    subtitle: 'ICC Criteria',
    stateKey: 'mfPrefibCriteria',
    evaluate: evaluatePrimaryMf,
  },
  postPv: {
    criteria: mfPostPvDiagnosticCriteria,
    subtitle: 'IWG-MRT Criteria',
    stateKey: 'mfPostPvCriteria',
    evaluate: evaluatePostMf,
    majorTitle: 'Required',
    minorTitle: 'Additional Criteria (two required)',
  },
  postEt: {
    criteria: mfPostEtDiagnosticCriteria,
    subtitle: 'IWG-MRT Criteria',
    stateKey: 'mfPostEtCriteria',
    evaluate: evaluatePostMf,
    majorTitle: 'Required',
    minorTitle: 'Additional Criteria (two required)',
  },
};

const MfDiagnosticCriteria: React.FC = () => {
  const { state, update } = useUserInput();
  const selected = state.selectedMfDiagnosis;
  const section = SECTION_MAP[selected];

  return (
    <div className="mf-diagnosis">
      <div className="hema-input-group">
        <label className="hema-label" htmlFor="mf-diagnosis-type">Diagnosis Type</label>
        <div className="hema-field">
          <select
            id="mf-diagnosis-type"
            value={selected}
            onChange={(e) => update('selectedMfDiagnosis', e.target.value as MfDiagnosisId)}
          >
            {OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <DiagnosticCriteriaTable
        criteria={section.criteria}
        subtitle={section.subtitle}
        stateKey={section.stateKey}
        majorTitle={section.majorTitle}
        minorTitle={section.minorTitle}
        evaluateDiagnosis={section.evaluate}
      />
    </div>
  );
};

export default MfDiagnosticCriteria;
